import React, { useMemo } from 'react';
import { Abgangsleitung, Hausanschluss, Segment } from '../../../../types/network';
import { useNetworkElementsStore } from '../../../../store/networkElementsStore';
import { useSettingsStore } from '../../../../store/settingsStore';
import { useStressTestStore } from '../../../../store/stressTestStore';
import { useViewMode } from '../../../../context/useViewMode';
import { findCablesConnectedToHausanschluss, findSegmentsConnectedToHausanschluss } from '../../../../utils/networkConnectionUtils';
import { LoadLevelThresholds } from '../../../../services/settingsService';
import { fmt } from '../../../../utils/numberFormat';
import { cableDisplayName } from '../../../../utils/elementDisplayName';
import { maxPhaseCurrentA } from '../../../../utils/phaseCurrent';

interface ConnectedLineSectionProps {
  hausanschluss: Hausanschluss;
}

const getLoadStyles = (loadPercentage: number, thresholds: { green: number; yellow: number; orange: number; red: number }) => {
  if (!thresholds) return { bg: 'bg-gray-50 dark:bg-primary-700', text: 'text-gray-700 dark:text-primary-300', bar: 'bg-gray-400' };

  if (loadPercentage >= thresholds.red) {
    return { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', bar: 'bg-red-500' };
  }
  if (loadPercentage >= thresholds.orange) {
    return { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', bar: 'bg-orange-500' };
  }
  if (loadPercentage >= thresholds.yellow) {
    return { bg: 'bg-yellow-50 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', bar: 'bg-yellow-500' };
  }
  if (loadPercentage >= thresholds.green) {
    return { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', bar: 'bg-green-500' };
  }
  return { bg: 'bg-gray-50 dark:bg-primary-700', text: 'text-gray-700 dark:text-primary-300', bar: 'bg-gray-400' };
};

const getFuseLoadStyles = (pct: number, warning: number, critical: number) => {
  if (pct >= critical) return { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', bar: 'bg-red-500' };
  if (pct >= warning) return { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', bar: 'bg-orange-500' };
  return { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', bar: 'bg-green-500' };
};

// NH fuses sit per phase: the fuse loading follows the highest conductor (maxPhaseCurrentA).
// A fuse protects the cable it sits on, so it is judged on the cable levels (orange = warning, red = critical).
const computeFuseLoading = (currentA: number, fuse: { current: number; isInserted: boolean } | undefined, cableLevels: LoadLevelThresholds) => {
  if (!fuse || !fuse.isInserted || fuse.current <= 0) return null;
  const absCurrentA = Math.abs(currentA);
  const pct = (absCurrentA / fuse.current) * 100;
  return { pct, absCurrentA, styles: getFuseLoadStyles(pct, cableLevels.orange, cableLevels.red), fuseCurrent: fuse.current };
};

export const ConnectedLineSection: React.FC<ConnectedLineSectionProps> = ({ hausanschluss }) => {
  // All hooks must be called before any conditional returns (React Rules of Hooks)
  const elements = useNetworkElementsStore(state => state.elements);
  const segments = useNetworkElementsStore(state => state.segments);
  const storeThresholds = useSettingsStore(state => state.thresholds);
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  const fuseThresholds = storeThresholds.cableLoadLevels;

  // Find connected cables using busIndex from cable_segments
  const allCables = useMemo(() =>
    elements.filter((el): el is Abgangsleitung => el.type === 'abgangsleitung'),
    [elements]
  );

  const connectedCables = useMemo(() =>
    findCablesConnectedToHausanschluss(hausanschluss, allCables),
    [hausanschluss, allCables]
  );

  // Take the first connected cable
  const line = connectedCables[0];

  // Early return after all hooks have been called
  if (!line) return null;

  // Find segments connected to this Hausanschluss
  const connectedSegs = findSegmentsConnectedToHausanschluss(hausanschluss, line);

  const validThresholds = storeThresholds.cableLoadLevels;

  // Get the highest loading percentage from connected segments
  const getMaxLoadingPercent = (segs: Segment[]) => {
    if (!segs || segs.length === 0) return 0;

    let maxLoading = 0;
    for (const segment of segs) {
      const segmentData = segments[segment.id];
      if (segmentData?.loadingPercent && segmentData.loadingPercent > maxLoading) {
        maxLoading = segmentData.loadingPercent;
      }
    }
    return maxLoading;
  };

  const firstConnectedSeg = connectedSegs[0];
  const segmentData = firstConnectedSeg ? segments[firstConnectedSeg.id] : null;
  const maxLoadingPercent = getMaxLoadingPercent(connectedSegs);
  const isEnabled = line.isEnabled;

  const cableType = String(line.metadata?.Type || line.cableTypeString || 'N/A');

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      {/* Left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 dark:bg-amber-500" />

      <div className="pl-4 pr-3 py-3 space-y-3">
        {/* Header: Cable type title + status dot */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mt-0.5">
              <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{cableType}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={`Kabel-ID ${line.id}`}>
                {cableDisplayName(line, id => elements.find(e => e.id === id))}
                <span className="mx-1">·</span>
                {fmt(line.totalLength, 1)} m
                <span className="mx-1">·</span>
                {line.crossSection} mm²
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 mt-1">
            <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400'}`} />
            <span className={`text-xs ${isEnabled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {isEnabled ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
        </div>

        {/* Power Metrics */}
        {(() => {
          const stressSegData = isStressTestMode && firstConnectedSeg && stressTestResults?.results
            ? stressTestResults.results[firstConnectedSeg.id]
            : null;

          if (isStressTestMode && stressSegData) {
            const stLoadStyles = getLoadStyles(stressSegData.loadingPercent ?? 0, validThresholds);
            const loadPct = stressSegData.loadingPercent ?? 0;
            const stressFuseLoading = computeFuseLoading(maxPhaseCurrentA(stressSegData), line.fuse, fuseThresholds);
            return (
              <div className="space-y-2">
                {/* Cable loading - prominent */}
                <div className={`${stLoadStyles.bg} rounded-md px-3 py-2`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-300">Kabelauslastung</span>
                    <span className={`text-lg font-bold ${stLoadStyles.text}`}>{fmt(loadPct, 1)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${stLoadStyles.bar} transition-all`} style={{ width: `${Math.min(loadPct, 100)}%` }} />
                  </div>
                </div>

                {/* Fuse loading */}
                {stressFuseLoading && (
                  <div className={`${stressFuseLoading.styles.bg} rounded-md px-3 py-2`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600 dark:text-gray-300">Sicherungsauslastung</span>
                      <span className={`text-lg font-bold ${stressFuseLoading.styles.text}`}>{fmt(stressFuseLoading.pct, 1)}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${stressFuseLoading.styles.bar} transition-all`} style={{ width: `${Math.min(stressFuseLoading.pct, 100)}%` }} />
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                      {fmt(stressFuseLoading.absCurrentA, 1)} A / {stressFuseLoading.fuseCurrent} A ({line.fuse!.type})
                    </div>
                  </div>
                )}

                {/* Power + Current compact row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {fmt(Math.abs(stressSegData.powerKw ?? 0), 1)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kW</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Leistung</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {fmt(maxPhaseCurrentA(stressSegData), 1)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">A</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Strom (max. Phase)</div>
                  </div>
                </div>

                {/* Per-phase currents */}
                {(stressSegData.currentL1A !== undefined || stressSegData.currentL2A !== undefined || stressSegData.currentL3A !== undefined) && (
                  <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Phasenströme</div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500">L1</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {stressSegData.currentL1A !== undefined ? `${fmt(stressSegData.currentL1A, 1)} A` : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500">L2</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {stressSegData.currentL2A !== undefined ? `${fmt(stressSegData.currentL2A, 1)} A` : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500">L3</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {stressSegData.currentL3A !== undefined ? `${fmt(stressSegData.currentL3A, 1)} A` : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Live mode
          const loadStyles = getLoadStyles(maxLoadingPercent, validThresholds);
          const loadPct = maxLoadingPercent;
          const liveFuseLoading = computeFuseLoading(maxPhaseCurrentA(segmentData), line.fuse, fuseThresholds);
          const calcTs = segmentData?.calculatedAt;
          return (
            <div className="space-y-2">
              {/* Calculation timestamp */}
              {calcTs != null && calcTs > 0 && (
                // Date included: in historical playback this instant can be
                // months in the past — a bare time of day was misleading.
                <div className="text-[10px] text-gray-400 dark:text-gray-500 text-right tabular-nums">
                  Berechnung: {new Date(calcTs).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}{' '}
                  {new Date(calcTs).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              {/* Cable loading - prominent */}
              <div className={`${loadStyles.bg} rounded-md px-3 py-2`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-300">Kabelauslastung</span>
                  <span className={`text-lg font-bold ${loadStyles.text}`}>{fmt(loadPct, 1)}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${loadStyles.bar} transition-all`} style={{ width: `${Math.min(loadPct, 100)}%` }} />
                </div>
              </div>

              {/* Fuse loading */}
              {liveFuseLoading && (
                <div className={`${liveFuseLoading.styles.bg} rounded-md px-3 py-2`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-300">Sicherungsauslastung</span>
                    <span className={`text-lg font-bold ${liveFuseLoading.styles.text}`}>{fmt(liveFuseLoading.pct, 1)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${liveFuseLoading.styles.bar} transition-all`} style={{ width: `${Math.min(liveFuseLoading.pct, 100)}%` }} />
                  </div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                    {fmt(liveFuseLoading.absCurrentA, 1)} A / {liveFuseLoading.fuseCurrent} A ({line.fuse!.type})
                  </div>
                </div>
              )}

              {/* Power + Current compact row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {fmt(Math.abs(segmentData?.powerKVA ?? segmentData?.powerKW ?? 0), 2)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kVA</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Leistung</div>
                </div>
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {fmt(maxPhaseCurrentA(segmentData), 2)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">A</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Strom (max. Phase)</div>
                  {(segmentData?.currentL1A != null || segmentData?.currentL2A != null || segmentData?.currentL3A != null) && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 tabular-nums">
                      L1 {segmentData?.currentL1A != null ? fmt(Math.abs(segmentData.currentL1A), 1) : '—'} · L2 {segmentData?.currentL2A != null ? fmt(Math.abs(segmentData.currentL2A), 1) : '—'} · L3 {segmentData?.currentL3A != null ? fmt(Math.abs(segmentData.currentL3A), 1) : '—'} A
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
